import type { RoleGroup } from '../../../stores/onboardingV2Store'
import ReviewContentQuestion from './ReviewContentQuestion'
import ReviewMetadataQuestion from './ReviewMetadataQuestion'
import ReviewProofCard from './ReviewProofCard'

interface Props {
  role: RoleGroup
  expanded: boolean
  onToggle: () => void
  onEditProof: (proofId: string, canonicalDescription: string) => Promise<void>
  onDiscardProof: (proofId: string) => Promise<void>
  onAnswerQuestion: (questionId: string, value: string, customValue?: string) => Promise<void>
  onSkipQuestion: (questionId: string) => Promise<void>
}

export default function ReviewRoleCard({
  role,
  expanded,
  onToggle,
  onEditProof,
  onDiscardProof,
  onAnswerQuestion,
  onSkipQuestion,
}: Props) {
  const proofCount = role.proofs.length
  const openQuestions = role.questions.length

  return (
    <div className="border border-ob-secondary/20 bg-ob-surface-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-start"
      >
        <span className="min-w-0">
          <span className="block truncate font-serif text-lg font-semibold text-ob-ink">
            {role.employer} · {role.role_title}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-body text-sm text-ob-secondary">
            {role.period && <span>{role.period}</span>}
            <span aria-hidden="true">·</span>
            <span>{proofCount} {proofCount === 1 ? 'proof' : 'proofs'}</span>
            {role.has_unresolved_mandatory_questions && (
              <span className="inline-flex items-center gap-1 text-ob-accent">
                <span aria-hidden="true">·</span>
                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">warning</span>
                {openQuestions} to resolve
              </span>
            )}
          </span>
        </span>
        <span
          className="material-symbols-outlined shrink-0 text-ob-secondary transition-transform"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>

      {expanded && (
        <div className="border-t border-ob-secondary/20 p-4">
          {role.questions.length > 0 && (
            <div className="mb-4 space-y-3">
              {role.questions.map((q) =>
                q.type === 'metadata_contradiction' ? (
                  <ReviewMetadataQuestion
                    key={q.question_id}
                    question={q}
                    onAnswer={(value, customValue) => onAnswerQuestion(q.question_id, value, customValue)}
                  />
                ) : (
                  <ReviewContentQuestion
                    key={q.question_id}
                    question={q}
                    onAnswer={(value) => onAnswerQuestion(q.question_id, value)}
                    onSkip={() => onSkipQuestion(q.question_id)}
                  />
                ),
              )}
            </div>
          )}

          <div className="divide-y divide-ob-secondary/20 border border-ob-secondary/20">
            {role.proofs.map((proof) => (
              <ReviewProofCard
                key={proof.proof_id}
                proof={proof}
                onEdit={(desc) => onEditProof(proof.proof_id, desc)}
                onDiscard={() => onDiscardProof(proof.proof_id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
